import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { openMapInNewTab } from "../../ShowMap/openMapTab";
import _, { cloneDeep } from "lodash";
import { v4 as uuidv4 } from "uuid";
import {
  getOrganizationId,
  toastError,
  toastSuccess,
} from "../../../../../utils";
import {
  addVesselVesselPortDistanceToOperations,
  convertPortOperationToDistanceString,
  fleetWithCalculatorDataMapping,
  calculationValidation,
  addPortDistanceToSelectedFleet,
} from "../../../VesselVesselMatrix/dataMapping";
import { placeAddedOperation } from "../../dataMapping";
import {
  initialCreatePortOperation,
  isSingleOperationTaken,
  singleInstanceOperations,
  termsCoEfficientTable,
} from "../../../../../constants";
import { initialVesselContractFields } from "../../Contracts";
import { useVesselVesselContractStore } from "../state/useVesselVesselContractStore";
import {
  getEstimateContractByIdApi,
  updatePortDistanceForContractApi,
  resetPortDistanceForContractApi,
  estimateContractcargoPriceApi,
  updateEstimateCostApi,
  deleteCargoFromEstimateByIdApi,
  addCargoForEstimateApi,
  createEstimateContractApi,
  updateVesselContractApi,
  updateVesselPortDistanceForContractApi,
} from "../services/vesselVesselContractApi";

export function useVesselVesselContractController(props = {}) {
  const {
    setIsFullScreen = () => {},
    setLoader = () => {},
    loader = false,
    vesselId: propVesselId = "",
    tabs = [],
    active = 0,
    handleMainTabChange = () => {},
    isViewOnly = false,
    setIsRevised = () => {},
  } = props;

  const { id: routeId = "" } = useParams();
  const id = routeId;
  const organizationId = getOrganizationId();

  const store = useVesselVesselContractStore();
  const {
    calculatorData,
    setCalculatorData,
    selectedVessels,
    setSelectedVessels,
    syncBunker,
    setSyncBunker,
    isOverride,
    setIsOverride,
    portDistanceResponse,
    setPortDistanceResponse,
    realPortDistanceResponse,
    setRealPortDistanceResponse,
    portDistancePayloadResponse,
    setPortDistancePayloadResponse,
    vesselOutContract,
    setVesselOutContract,
    rawEstimateContract,
    setRawEstimateContract,
    isPrivate,
    setIsPrivate,
  } = store;

  const stateRef = useRef(store);
  stateRef.current = store;

  const fetchPortDistance = useRef(true);

  const [hideOrder, setHideOrder] = useState(false);
  const [hideCalculator, setHideCalculator] = useState(false);
  const [allQueryFormat, setAllQueryFormat] = useState([]);
  const [selectedQuery, setSelectedQuery] = useState({});
  const [fleetCheckbox, setFleetCheckbox] = useState({
    ballest: { 1: true, 2: true, 3: true, 4: true },
    laden: { 1: true, 2: true, 3: true, 4: true },
  });

  // modals
  const [marginModal, setMarginModal] = useState(false);
  const [supplyModal, setSupplyModal] = useState(false);
  const [splitHireModal, setSplitHireModal] = useState(false);
  const [splitHireModalVesselOut, setSplitHireModalVesselOut] = useState(false);
  const [selectedSplitHire, setSelectedSplitHire] = useState({});
  const [selectedSplitHireVesselOut, setSelectedSplitHireVesselOut] = useState({});
  const [openAddOperation, setOpenAddOperation] = useState(false);
  const [openAddOperationIndex, setOpenAddOperationindex] = useState(false);

  // contract editing
  const [editVesselContractModelOpen, setEditVesselContractModelOpen] = useState(false);
  const [vesselFormData, setVesselFormData] = useState({
    vesselIn: initialVesselContractFields,
    vesselOut: initialVesselContractFields,
    type: "vesselIn",
  });
  const [vesselAttachments, setVesselAttachments] = useState([]);
  const [vesselTextContract, setVesselTextContract] = useState(null);
  const [prevAttachments, setPrevAttachments] = useState({
    attachments: [],
    textContract: "",
  });

  // copy to estimation
  const [estimationCopyModelOpen, setEstimationCopyModelOpen] = useState(false);
  const [inputValue, setInputValue] = useState();

  const delayUpdateAndSaveOperation = useCallback(
    _.debounce((data) => onUpdateAndSavePortDistance(data), 500),
    []
  );

  // ========== INITIAL DATA FETCH ==========

  useEffect(() => {
    setIsFullScreen(true);
    getInitalQueryAndFleetData(id || propVesselId);
    onChangeOverride(true);
    return () => {
      setIsFullScreen(false);
    };
  }, []);

  const getInitalQueryAndFleetData = (contractId) => {
    setLoader(true, "Fetching vessel contract data...");
    Promise.all([getEstimateContractByIdApi({ id: contractId })])
      .then((res) => {
        const response1 = res[0]?.data;
        setRawEstimateContract(res[0]?.data);
        setIsPrivate(res[0]?.data?.access === "public" ? false : true);
        // Locked first estimate (after revision) shows the snapshot taken at ops creation
        const snapshot =
          response1?.vesselContractDetails?.operationVesselContract;
        const vesselContract =
          isViewOnly && snapshot?.vesselDetails?.clientQuery
            ? snapshot
            : response1?.vesselContractDetails;
        // Older contracts saved an empty vesselOutContract; fall back to vesselDetails.vesselOut
        const { vesselOut: inVesselOut, clientQuery: _cq, ...inBase } =
          vesselContract?.vesselDetails || {};
        const outDetails = Object.fromEntries(
          Object.entries(
            vesselContract?.vesselOutContract?.vesselDetails || {}
          ).filter(([, v]) => v !== undefined && v !== "")
        );
        setInitialFetchedData(
          {
            ...vesselContract?.vesselDetails,
            vesselOut: { ...inBase, ...inVesselOut, ...outDetails },
          },
          true
        );
        setVesselOutContract(vesselContract?.vesselOutContract);
      })
      .catch((err) => {
        setLoader(false);
        toastError(err?.data?.message);
      });
  };

  // ========== CALCULATION ERRORS ==========

  const calculationErrors = useMemo(() => {
    let errors = [];
    if (
      selectedVessels &&
      calculatorData.portOperation.length &&
      calculatorData.fleetList.length
    ) {
      errors = calculationValidation(calculatorData, selectedVessels);
    }
    return errors;
  }, [calculatorData, selectedVessels]);

  // ========== INITIAL DATA PROCESSING ==========

  const setInitialFetchedData = async (mainRes1, initial = false) => {
    fetchPortDistance.current = true;
    let selectedQueryLocal = {};
    setAllQueryFormat(mainRes1?.clientQuery);
    selectedQueryLocal = mainRes1?.clientQuery;
    setSelectedQuery(selectedQueryLocal);
    if (mainRes1?._id) {
      if (!loader) setLoader(true, "Processing initial data...");

      let combinedCargo = mainRes1?.clientQuery?.cargoList;
      let formattedPortOperations = combinedCargo.map((portElement, i) => ({
        ...portElement,
        index: i,
      }));

      if (!selectedQueryLocal.fuelCalculationMethod)
        selectedQueryLocal.fuelCalculationMethod = "DEFAULT";
      if (!selectedQueryLocal.fuelType) selectedQueryLocal.fuelType = "TYPE0";

      if (
        selectedQueryLocal.fuelCalculationMethod === "LIFO" ||
        selectedQueryLocal.fuelCalculationMethod === "FIFO" ||
        selectedQueryLocal.fuelCalculationMethod === "AVERAGE"
      ) {
        setSyncBunker(true);
      }

      if (selectedQueryLocal.fuelCalculationMethod === "DEFAULT") {
        const currentType = selectedQueryLocal.fuelType.replace("TYPE", "");
        formattedPortOperations = formattedPortOperations.map((item) => {
          return {
            ...item,
            cargoDetails: {
              ...item?.cargoDetails,
              fuelPriceType0Hsfo: 0,
              fuelPriceType0Lsfo: 0,
              fuelPriceType0Mgo: 0,
              fuelPriceType1Hsfo: 0,
              fuelPriceType1Lsfo: 0,
              fuelPriceType1Mgo: 0,
              fuelPriceType2Hsfo: 0,
              fuelPriceType2Lsfo: 0,
              fuelPriceType2Mgo: 0,
              fuelPriceType3Hsfo: 0,
              fuelPriceType3Lsfo: 0,
              fuelPriceType3Mgo: 0,
              fuelPriceType4Hsfo: 0,
              fuelPriceType4Lsfo: 0,
              fuelPriceType4Mgo: 0,
            },
          };
        });
      }

      const updatedData = {
        ...stateRef.current.calculatorData,
        clientQuery: selectedQueryLocal,
        portOperation: formattedPortOperations,
      };
      const { clientQuery, ...fleetList } = mainRes1;
      if (initial) updatedData.fleetList = cloneDeep([fleetList]);
      // sendCalculationRequest is async and owns the loader from here (it clears it when the
      // calculation ends); clearing it here would hide the loader during the distance fetch.
      sendCalculationRequest(updatedData).catch((err) => {
        setLoader(false);
        toastError(err?.message || "Something went wrong.");
      });
    } else {
      setLoader(false);
    }
  };

  // ========== PORT OPERATIONS ==========

  const onChangePortOperations = (
    index,
    e,
    cargoUpdate = false,
    isDate = false,
    datename
  ) => {
    let value = "";
    let name = "";
    if (isDate) {
      value = e;
      name = datename;
    } else {
      value = e.target?.value;
      name = e.target?.name;
    }
    let portOperation = [...stateRef.current.calculatorData.portOperation];

    if (
      name === "operation" &&
      (singleInstanceOperations.includes(portOperation[index]?.operation) ||
        isSingleOperationTaken(portOperation, value))
    ) {
      return toastError("Delivery-out and Redelivery-in cannot be edited or duplicated.");
    }

    if (name === "terms") {
      const termFactor = termsCoEfficientTable.find(
        (item) => item.label === value
      );
      portOperation[index]["terms"] = value;
      portOperation[index]["factor"] = (termFactor && termFactor.value) || 0;
    } else if (cargoUpdate) {
      if (!portOperation[index].cargoDetails) {
        portOperation[index].cargoDetails = {};
      }
      portOperation[index].cargoDetails[name] = value;
    } else {
      portOperation[index][name] = value;
    }

    let updatedCalculatorData = {
      ...stateRef.current.calculatorData,
      portOperation,
    };
    if (
      !cargoUpdate &&
      updatedCalculatorData?.clientQuery?.portToPortDistance?.output &&
      portOperation[index - 1] &&
      portOperation[index]?.port
    ) {
      const key = `${portOperation[index - 1]?.port}_${portOperation[index].port}`;
      if (updatedCalculatorData.clientQuery.portToPortDistance.output[key]) {
        updatedCalculatorData.clientQuery.portToPortDistance.output[key][name] =
          value;
      }
    }
    if (name === "distanceNormal" || name === "distanceECA") {
      const updateData = {
        distEca: portOperation[index].distanceECA,
        distNormal: portOperation[index].distanceNormal,
        id: portOperation[index]._id,
      };
      const saveData = {
        distEca: portOperation[index].distanceECA,
        distNormal: portOperation[index].distanceNormal,
        openingPort:
          (portOperation[index - 1] && portOperation[index - 1]["port"]) || "",
        endingPort: portOperation[index]["port"] || "",
      };
      delayUpdateAndSaveOperation({
        updateData,
        saveData,
        updatedCalculatorData,
      });
    }
    setCalculatorData(updatedCalculatorData);
  };

  const onUpdateAndSavePortDistance = ({
    updateData = {},
    saveData = {},
    updatedCalculatorData = {},
  }) => {
    setLoader(true, "Saving port distance details...");
    Promise.all([updatePortDistanceForContractApi(saveData)])
      .then(() => {
        setLoader(false);
        sendCalculationRequest(updatedCalculatorData);
      })
      .catch((err) => {
        setLoader(false);
        toastError(err?.data?.message || "Something went wrong.");
      });
  };

  // ========== CALCULATION PIPELINE ==========

  const sendCalculationRequest = async (sendData) => {
    let portDistanceRes =
      sendData?.portDistanceResponse || stateRef.current.portDistanceResponse;
    let realData =
      sendData?.clientQuery?.portToPortDistanceReal?.output ||
      stateRef.current.realPortDistanceResponse;
    let payload =
      sendData?.clientQuery?.portToPortDistanceReal?.payload ||
      stateRef.current.portDistancePayloadResponse;

    // sendData.portDistanceResponse means the caller (e.g. port reorder) just fetched fresh
    // distances; refetching here would overwrite them with the older saved ones.
    if (fetchPortDistance.current && sendData?.portDistanceResponse) {
      fetchPortDistance.current = false;
    }
    if (fetchPortDistance.current) {
      const resObj = await onFetchAndSetIntitalPortDistance(sendData);
      if (resObj && resObj.error) {
        return;
      }
      portDistanceRes = resObj?.data;
      realData = resObj?.realData;
      payload = resObj?.payload;
    }
    if (!sendData?.clientQuery?.portToPortDistance) {
      getInitalQueryAndFleetData(id || propVesselId);
      return;
    }

    sendData = {
      ...sendData,
      clientQuery: {
        ...sendData.clientQuery,
        portToPortDistanceReal: {
          output: realData,
          payload,
        },
        portToPortDistance: {
          output: portDistanceRes,
          payload,
        },
      },
      portOperation: addVesselVesselPortDistanceToOperations(
        portDistanceRes,
        [...sendData.portOperation],
        realData
      ),
    };

    let fleetSelected = {};
    const updatedFleedIds = sendData.fleetList.map((fleetDetails) => ({
      ...fleetDetails,
      vesselUniqueId: uuidv4(),
    }));

    const cargoList = [];
    const cargoExtraDetails = [];

    (sendData.portOperation || []).forEach((item) => {
      const op = String(item.operation || "").toLowerCase();
      const isCoreOperation =
        op.includes("commence") ||
        op.includes("loading") ||
        op.includes("discharging") ||
        op.includes("load") ||
        op.includes("disch") ||
        !item.isExtra;

      const sanitizedItem = {
        ...item,
        loadline: item.loadline || "S-Summer",
        sulfur: item.sulfur || "Normal",
      };

      if (isCoreOperation) {
        cargoList.push({
          ...sanitizedItem,
          isExtra: false,
        });
      } else {
        cargoExtraDetails.push({
          ...sanitizedItem,
          isExtra: true,
        });
      }
    });

    let modifiedData = {
      cargoList:
        cargoList.length > 0
          ? cargoList
          : sendData.portOperation.map((item) => ({ ...item, isExtra: false })),
      cargoExtraDetails: cargoList.length > 0 ? cargoExtraDetails : [],
      fleetList: cloneDeep(updatedFleedIds),
      clientQuery: {
        ...sendData.clientQuery,
        calculationFormat: sendData.portOperation
          .map((item) => item._id)
          .join(","),
      },
      autoSave: false,
    };

    await calculationValidation(modifiedData, stateRef.current.selectedVessels);

    if (!loader) setLoader(true, "Calculating pricing and costs...");
    const res = await sendCalculatorResponse(modifiedData);
    setLoader(false);

    const fleetData = fleetWithCalculatorDataMapping(
      cloneDeep(updatedFleedIds),
      res?.data
    );

    if (
      stateRef.current.selectedVessels &&
      stateRef.current.selectedVessels._id
    ) {
      fleetSelected =
        fleetData.find(
          (item) => item._id === stateRef.current.selectedVessels._id
        ) || {};
    } else {
      fleetSelected = fleetData[0];
    }

    onChangeFleetRowSelect(
      fleetSelected,
      { ...sendData, fleetList: fleetData },
      true,
      true,
      true
    );

    const newUpdatedQuery = {
      ...sendData?.clientQuery,
      qtyMode: sendData?.clientQuery?.qtyMode || "",
      fuelPriceOverridden:
        sendData?.clientQuery?.fuelPriceOverridden || false,
      cargoList: sendData?.portOperation || [],
      portToPortDistanceReal:
        sendData?.clientQuery?.portToPortDistanceReal || {},
      portToPortDistance: sendData?.clientQuery?.portToPortDistance || {},
    };

    const newUpdatedFleet = { ...fleetSelected };

    if (sendData?.autoSave) {
      saveAllResultUpdatedData({
        newUpdatedFleet,
        newUpdatedQuery,
        contractType: "vessel-vessel",
        disableRefetch: sendData?.disableRefetch || false,
      });
    }
  };

  const saveAllResultUpdatedData = (updatedData) => {
    const { disableRefetch = false, ...restClientQuery } = updatedData;
    if (isViewOnly) return;
    const isOperation = !!propVesselId;
    updateEstimateCostApi({
      contractId: id || propVesselId,
      ...restClientQuery,
      ...(isOperation && { isOperation: true, isRevised: true }),
    })
      .then((res) => {
        if (res && isOperation) setIsRevised(true);
        if (res && !disableRefetch) {
          toastSuccess("Calculation updated and saved successfully!");
        }
      })
      .catch((err) => {
        setLoader(false);
        toastError(err?.data?.message);
      });
  };

  const sendCalculatorResponse = (modifiedData = {}) => {
    return new Promise((resolve, reject) => {
      try {
        if (!modifiedData?.fleetList?.length) {
          return resolve({});
        }

        const updatedCargoList = (modifiedData.cargoList || []).map(
          (cargoDetails = {}) => {
            const cargoDetailObj = cargoDetails.cargoDetails || {};
            const { _id, ...restCargoMainDetails } = cargoDetails;
            return {
              ...restCargoMainDetails,
              cargoDetails: { ...cargoDetailObj },
            };
          }
        );

        const clientQuery = modifiedData.clientQuery || {};
        const {
          _id,
          cargoList: queryCargoList,
          createdUser,
          lastModifiedUser,
          userId,
          calculationFormat,
          __v,
          createdAt,
          updatedAt,
          isCalculate,
          ...restClientQuery
        } = clientQuery;

        const updatedClientQuery = {
          ...restClientQuery,
          cargoList: queryCargoList,
        };

        const updatedFleetListData = (modifiedData.fleetList || []).map(
          (fleetDetails = {}, index) => {
            const vesselDetails = fleetDetails?.vessel
              ? JSON.parse(fleetDetails.vessel)
              : {};

            const {
              _id: parseVesselId,
              userId: parseVesselUserId,
              createdUser: parseVesselCreatedUser,
              lastModifiedUser: parseVesselModifiedUser,
              vesselExtraDetails,
              __v: parseVessel__v,
              createdAt,
              createdDate,
              lastModifiedDate,
              pattern,
              updatedAt,
              vesselLabel,
              vesselAHL,
              vesselCO2,
              vesselCountry,
              vesselGrabs,
              vesselIceClass,
              vesselIMO,
              vesselLdnConsmMGO1,
              vesselLdnConsmMGO2,
              vesselLightShip,
              vesselMMSI,
              vesselType,
              vesselSource,
              vesselName,
              ...parseVesselDetails
            } = vesselDetails;

            const {
              _id: fleetId,
              vesselId: fleetVesselId,
              createdUser: fleetCreatedUser,
              organizationId,
              userId: fleetUserId,
              lastModifiedUser: fleetLastModifiedUser,
              vesselUniqueId = uuidv4(),
              __v: fleet__v,
              ...restFleetDetails
            } = fleetDetails;

            return {
              ...restFleetDetails,
              vessel: JSON.stringify({
                id: vesselUniqueId,
                ...parseVesselDetails,
                system: true,
              }),
              vesselId: vesselUniqueId,
            };
          }
        );

        const updatedCargoExtraDetails = (
          modifiedData.cargoExtraDetails || []
        ).map((cargoextraDetails = {}) => {
          const extraCargoDetails = cargoextraDetails.cargoDetails || {};
          const { _id, ...restCargoMainDetails } = cargoextraDetails;
          return {
            ...restCargoMainDetails,
            cargoDetails: { ...extraCargoDetails },
          };
        });

        const calculationData = {
          autoSave: modifiedData.autoSave || false,
          cargoExtraDetails: updatedCargoExtraDetails,
          cargoList: updatedCargoList,
          clientQuery: updatedClientQuery,
          fleetList: updatedFleetListData,
        };

        estimateContractcargoPriceApi(calculationData)
          .then((res) => resolve(res))
          .catch((err) => {
            console.error("Calculation error:", err);
            toastError(err?.data?.message || "Something went wrong");
            resolve({});
          });
      } catch (error) {
        console.error("Error in sendCalculatorResponse:", error);
        toastError("An unexpected error occurred");
        resolve({});
      }
    });
  };

  // ========== FLEET ROW SELECT ==========

  const onChangeFleetRowSelect = async (
    fleet = {},
    resultCalculatorData = null,
    changeVessel = false,
    isPortDistanceChanged = false,
    isFromCalculateButton = false
  ) => {
    let updatedCalculatorData = {};
    if (resultCalculatorData) updatedCalculatorData = resultCalculatorData;
    else updatedCalculatorData = { ...stateRef.current.calculatorData };
    let commencePort = (fleet && fleet.port) || "";
    let fleetFuelType =
      (fleet && fleet.vessel && JSON.parse(fleet.vessel)) || {};
    fleetFuelType = fleetFuelType.fuelType || "TYPE0";
    let portOperation = [...updatedCalculatorData.portOperation];

    const portToPortOperationResultMap =
      (fleet && fleet.portToPortOperationResultMap) || [];
    portOperation = portOperation.map((operation, index) => {
      const selectedResultMap = portToPortOperationResultMap[index] || {};
      const removeDistanceFromResultMap = ({
        distanceECA = "",
        distanceNormal = "",
        ...rest
      }) => {
        return { ...rest };
      };
      if (index !== 0) commencePort = (operation && operation.port) || "";
      let isTermExists = termsCoEfficientTable[0];
      if (operation.terms) {
        isTermExists = termsCoEfficientTable.find(
          (item) => item.label === operation.terms
        );
      }
      let cargoDetails = { ...operation.cargoDetails };

      if (index === 0) {
        const currentType = fleetFuelType.replace("TYPE", "");
        const {
          bnkDelhs = 0,
          bnkDells = 0,
          bnkDelmgo = 0,
          bnkPriceDeliveryhs = 0,
          bnkPriceDeliveryls = 0,
          bnkPriceDeliverymgo = 0,
        } = fleet;
        cargoDetails = {
          ...cargoDetails,
          bunkerSuppliedHSFO: bnkDelhs,
          bunkerSuppliedLSFO: bnkDells,
          bunkerSuppliedMGO: bnkDelmgo,
          [`fuelPriceType${currentType}Hsfo`]: bnkPriceDeliveryhs,
          [`fuelPriceType${currentType}Lsfo`]: bnkPriceDeliveryls,
          [`fuelPriceType${currentType}Mgo`]: bnkPriceDeliverymgo,
        };
      }
      return {
        ...operation,
        ...removeDistanceFromResultMap(selectedResultMap),
        cargoDetails,
        port: commencePort,
        terms: isTermExists.label,
        factor: isTermExists.value,
      };
    });

    if (!isPortDistanceChanged) {
      let portDistanceRes = stateRef.current.portDistanceResponse;
      if (fetchPortDistance.current) {
        const resObj = await onFetchAndSetIntitalPortDistance(
          updatedCalculatorData
        );
        if (resObj && resObj.error) {
          return;
        }
        portDistanceRes = resObj?.data;
      }
      portOperation = addVesselVesselPortDistanceToOperations(portDistanceRes, [
        ...portOperation,
      ]);
    }
    // add port distance to selected fleet as well
    fleet = addPortDistanceToSelectedFleet(portOperation, fleet);
    const FuelHandOverProfitCount = VesselOutFuelCalculate(
      updatedCalculatorData,
      fleet
    );
    fleet.FuelHandOverProfitCount = FuelHandOverProfitCount;
    if (changeVessel || !stateRef.current.selectedVessels._id) {
      setSelectedVessels(fleet);
    }

    setCalculatorData({
      ...updatedCalculatorData,
      clientQuery: {
        ...updatedCalculatorData.clientQuery,
        fuelType: fleetFuelType,
      },
      fleetList: updatedCalculatorData.fleetList.map((singleFleet) => {
        if (singleFleet._id === fleet._id) return fleet;
        else return singleFleet;
      }),
      portOperation,
    });
  };

  // ========== VESSEL OUT FUEL CALCULATE ==========

  const VesselOutFuelCalculate = (updatedCalculatorData, fleet) => {
    const portOperation = updatedCalculatorData?.portOperation;
    const fleetDetails = fleet;

    let bunkerSuppliedHSFOTotal = 0;
    let bunkerSuppliedLSFOTotal = 0;
    let bunkerSuppliedMGOTotal = 0;

    let bunkerSuppliedHSFOSupplyInternalTotal = 0;
    let bunkerSuppliedLSFOSupplyInternalTotal = 0;
    let bunkerSuppliedMGOSupplyInternalTotal = 0;

    let hsReserveInternalTotal = 0;
    let lsReserveInternalTotal = 0;
    let mgoReserveInternalTotal = 0;

    let hsReserveExternalTotal = 0;
    let lsReserveExternalTotal = 0;
    let mgoReserveExternalTotal = 0;

    let hsFuelPriceReserveInternalTotal = 0;
    let lsFuelPriceReserveInternalTotal = 0;
    let mgoFuelPriceReserveInternalTotal = 0;

    const { fuelType = "" } = updatedCalculatorData?.clientQuery || {};
    const currentType = fuelType.replace("TYPE", "");

    let hsFuelPriceTotal = 0;
    let lsFuelPriceTotal = 0;
    let mgoFuelPriceTotal = 0;

    let hsFuelPriceSupplyInternalTotal = 0;
    let lsFuelPriceSupplyInternalTotal = 0;
    let mgoFuelPriceSupplyInternalTotal = 0;

    let totalPortExpense = 0;
    for (let i = 0; i < portOperation?.length; i++) {
      bunkerSuppliedHSFOTotal += Number(
        portOperation?.[i]?.cargoDetails?.bunkerSuppliedHSFO
      );
      bunkerSuppliedLSFOTotal += Number(
        portOperation?.[i]?.cargoDetails?.bunkerSuppliedLSFO
      );
      bunkerSuppliedMGOTotal += Number(
        portOperation?.[i]?.cargoDetails?.bunkerSuppliedMGO
      );

      bunkerSuppliedHSFOSupplyInternalTotal +=
        portOperation?.[i]?.cargoDetails?.paidBy === "Owner" &&
        portOperation?.[i]?.cargoDetails?.reserve === "Supply"
          ? Number(portOperation?.[i]?.cargoDetails?.bunkerSuppliedHSFO)
          : 0;
      bunkerSuppliedLSFOSupplyInternalTotal +=
        portOperation?.[i]?.cargoDetails?.paidBy === "Owner" &&
        portOperation?.[i]?.cargoDetails?.reserve === "Supply"
          ? Number(portOperation?.[i]?.cargoDetails?.bunkerSuppliedLSFO)
          : 0;
      bunkerSuppliedMGOSupplyInternalTotal +=
        portOperation?.[i]?.cargoDetails?.paidBy === "Owner" &&
        portOperation?.[i]?.cargoDetails?.reserve === "Supply"
          ? Number(portOperation?.[i]?.cargoDetails?.bunkerSuppliedMGO)
          : 0;

      hsReserveInternalTotal +=
        portOperation?.[i]?.cargoDetails?.paidBy === "Owner" &&
        portOperation?.[i]?.cargoDetails?.reserve === "Reserve"
          ? Number(portOperation?.[i]?.cargoDetails?.bunkerSuppliedHSFO)
          : 0;
      lsReserveInternalTotal +=
        portOperation?.[i]?.cargoDetails?.paidBy === "Owner" &&
        portOperation?.[i]?.cargoDetails?.reserve === "Reserve"
          ? Number(portOperation?.[i]?.cargoDetails?.bunkerSuppliedLSFO)
          : 0;
      mgoReserveInternalTotal +=
        portOperation?.[i]?.cargoDetails?.paidBy === "Owner" &&
        portOperation?.[i]?.cargoDetails?.reserve === "Reserve"
          ? Number(portOperation?.[i]?.cargoDetails?.bunkerSuppliedMGO)
          : 0;

      hsReserveExternalTotal +=
        portOperation?.[i]?.cargoDetails?.paidBy === "Operator" &&
        portOperation?.[i]?.cargoDetails?.reserve === "Reserve"
          ? Number(portOperation?.[i]?.cargoDetails?.bunkerSuppliedHSFO)
          : 0;
      lsReserveExternalTotal +=
        portOperation?.[i]?.cargoDetails?.paidBy === "Operator" &&
        portOperation?.[i]?.cargoDetails?.reserve === "Reserve"
          ? Number(portOperation?.[i]?.cargoDetails?.bunkerSuppliedLSFO)
          : 0;
      mgoReserveExternalTotal +=
        portOperation?.[i]?.cargoDetails?.paidBy === "Operator" &&
        portOperation?.[i]?.cargoDetails?.reserve === "Reserve"
          ? Number(portOperation?.[i]?.cargoDetails?.bunkerSuppliedMGO)
          : 0;

      // fuel price
      const currentHsfo =
        portOperation?.[i]?.cargoDetails[`fuelPriceType${currentType}Hsfo`] ||
        0;
      const currentLsfo =
        portOperation?.[i]?.cargoDetails[`fuelPriceType${currentType}Lsfo`] ||
        0;
      const currentMgo =
        portOperation?.[i]?.cargoDetails[`fuelPriceType${currentType}Mgo`] || 0;
      hsFuelPriceTotal +=
        Number(portOperation?.[i]?.cargoDetails?.bunkerSuppliedHSFO) *
        Number(currentHsfo);
      lsFuelPriceTotal +=
        Number(portOperation?.[i]?.cargoDetails?.bunkerSuppliedLSFO) *
        Number(currentLsfo);
      mgoFuelPriceTotal +=
        Number(portOperation?.[i]?.cargoDetails?.bunkerSuppliedMGO) *
        Number(currentMgo);

      hsFuelPriceSupplyInternalTotal +=
        portOperation?.[i]?.cargoDetails?.paidBy === "Owner" &&
        portOperation?.[i]?.cargoDetails?.reserve === "Supply"
          ? Number(portOperation?.[i]?.cargoDetails?.bunkerSuppliedHSFO) *
            Number(currentHsfo)
          : 0;
      lsFuelPriceSupplyInternalTotal +=
        portOperation?.[i]?.cargoDetails?.paidBy === "Owner" &&
        portOperation?.[i]?.cargoDetails?.reserve === "Supply"
          ? Number(portOperation?.[i]?.cargoDetails?.bunkerSuppliedLSFO) *
            Number(currentLsfo)
          : 0;
      mgoFuelPriceSupplyInternalTotal +=
        portOperation?.[i]?.cargoDetails?.paidBy === "Owner" &&
        portOperation?.[i]?.cargoDetails?.reserve === "Supply"
          ? Number(portOperation?.[i]?.cargoDetails?.bunkerSuppliedMGO) *
            Number(currentMgo)
          : 0;

      hsFuelPriceReserveInternalTotal +=
        portOperation?.[i]?.cargoDetails?.paidBy === "Owner" &&
        portOperation?.[i]?.cargoDetails?.reserve === "Reserve"
          ? Number(portOperation?.[i]?.cargoDetails?.bunkerSuppliedHSFO) *
            Number(currentHsfo)
          : 0;
      lsFuelPriceReserveInternalTotal +=
        portOperation?.[i]?.cargoDetails?.paidBy === "Owner" &&
        portOperation?.[i]?.cargoDetails?.reserve === "Reserve"
          ? Number(portOperation?.[i]?.cargoDetails?.bunkerSuppliedLSFO) *
            Number(currentLsfo)
          : 0;
      mgoFuelPriceReserveInternalTotal +=
        portOperation?.[i]?.cargoDetails?.paidBy === "Owner" &&
        portOperation?.[i]?.cargoDetails?.reserve === "Reserve"
          ? Number(portOperation?.[i]?.cargoDetails?.bunkerSuppliedMGO) *
            Number(currentMgo)
          : 0;

      totalPortExpense += Number(portOperation?.[i]?.portExp);
    }

    const voyConsumptionFuelQty = {
      hsQuantity: fleetDetails?.hsQuantity,
      lsQuantity: fleetDetails?.lsQuantity,
      mgoQuantity: fleetDetails?.mgoQuantity,
    };
    const supplyBalanceFuelQty = {
      hsSupplyBalance:
        bunkerSuppliedHSFOSupplyInternalTotal +
        Number(fleetDetails?.hsQuantity),
      lsSupplyBalance:
        bunkerSuppliedLSFOSupplyInternalTotal +
        Number(fleetDetails?.lsQuantity),
      mgoSupplyBalance:
        bunkerSuppliedMGOSupplyInternalTotal +
        Number(fleetDetails?.mgoQuantity),
    };
    const balanceFuelQty = {
      hsBalance:
        supplyBalanceFuelQty?.hsSupplyBalance + hsReserveInternalTotal,
      lsBalance:
        supplyBalanceFuelQty?.lsSupplyBalance + lsReserveInternalTotal,
      mgoBalance:
        supplyBalanceFuelQty?.mgoSupplyBalance + mgoReserveInternalTotal,
    };

    const voyConsumptionFuelPrice = {
      hsFuelPrice:
        Number(fleetDetails?.fuelHsExpense) /
        (voyConsumptionFuelQty?.hsQuantity || 1),
      lsFuelPrice:
        Number(fleetDetails?.fuelLsExpense) /
        (voyConsumptionFuelQty?.lsQuantity || 1),
      mgoFuelPrice:
        Number(fleetDetails?.fuelMgoExpense) /
        (voyConsumptionFuelQty?.mgoQuantity || 1),
    };
    const supplyBalanceFuelCost = {
      hsSupplyBalanceFuelCost:
        hsFuelPriceSupplyInternalTotal -
        parseInt(fleetDetails?.fuelHsExpense),
      lsSupplyBalanceFuelCost:
        lsFuelPriceSupplyInternalTotal -
        parseInt(fleetDetails?.fuelLsExpense),
      mgoSupplyBalanceFuelCost:
        mgoFuelPriceSupplyInternalTotal -
        parseInt(fleetDetails?.fuelMgoExpense),
    };

    const balanceFuelCost = {
      hsBalanceFuelCost:
        supplyBalanceFuelCost?.hsSupplyBalanceFuelCost +
        hsFuelPriceReserveInternalTotal,
      lsBalanceFuelCost:
        supplyBalanceFuelCost?.lsSupplyBalanceFuelCost +
        lsFuelPriceReserveInternalTotal,
      mgoBalanceFuelCost:
        supplyBalanceFuelCost?.mgoSupplyBalanceFuelCost +
        mgoFuelPriceReserveInternalTotal,
    };

    const fuelPrice = {
      hsFuelPrice:
        balanceFuelCost?.hsBalanceFuelCost / balanceFuelQty?.hsBalance,
      lsFuelPrice:
        balanceFuelCost?.lsBalanceFuelCost / balanceFuelQty?.lsBalance,
      mgoFuelPrice:
        balanceFuelCost?.mgoBalanceFuelCost / balanceFuelQty?.mgoBalance,
    };

    const deliveryOutFuelCost = {
      hsDeliveryOutFuelCost:
        balanceFuelQty?.hsBalance *
        Number(fleetDetails?.vesselOut?.bnkPriceDeliveryhs),
      lsDeliveryOutFuelCost:
        balanceFuelQty?.lsBalance *
        Number(fleetDetails?.vesselOut?.bnkPriceDeliveryls),
      mgoDeliveryOutFuelCost:
        balanceFuelQty?.mgoBalance *
        Number(fleetDetails?.vesselOut?.bnkPriceDeliverymgo),
    };

    const fuelHandOverProfit = {
      hsFuelHandOverProfit:
        deliveryOutFuelCost?.hsDeliveryOutFuelCost -
        balanceFuelCost?.hsBalanceFuelCost,
      lsFuelHandOverProfit:
        deliveryOutFuelCost?.lsDeliveryOutFuelCost -
        balanceFuelCost?.lsBalanceFuelCost,
      mgoFuelHandOverProfit:
        deliveryOutFuelCost?.mgoDeliveryOutFuelCost -
        balanceFuelCost?.mgoBalanceFuelCost,
    };
    const TotalFuelHandOverProfit =
      fuelHandOverProfit?.hsFuelHandOverProfit +
      fuelHandOverProfit?.lsFuelHandOverProfit +
      fuelHandOverProfit?.mgoFuelHandOverProfit;

    // result
    const totalDuration = fleetDetails?.vesselOut?.DurationOut;
    const splitHire = fleetDetails?.vesselOut?.splitHire
      ? JSON.parse(fleetDetails?.vesselOut?.splitHire)
      : [];

    let remainingDays = totalDuration;
    let splitHireAmount = 0;

    splitHire.forEach((item) => {
      if (remainingDays <= 0) return;
      const daysToApply = Math.min(remainingDays, item.NoDays);
      splitHireAmount += daysToApply * item.hireRate;
      remainingDays -= daysToApply;
    });

    const balanceDaysAfterSplit = remainingDays;
    const mainHireAmount =
      balanceDaysAfterSplit *
      Number(fleetDetails?.vesselOut?.hirePerDay);

    // revenue
    const TCOGrossHire =
      (splitHireAmount ? splitHireAmount : 0) +
      (mainHireAmount ? mainHireAmount : 0) +
      (fleetDetails?.vesselOut?.hireGbb
        ? Number(fleetDetails?.vesselOut?.hireGbb)
        : 0);
    const TCOHireCommission =
      (TCOGrossHire * Number(fleetDetails?.vesselOut?.tcEq || 1)) / 100;
    const miscRevenue = fleetDetails?.vesselOut?.miscRevenue
      ? Number(fleetDetails?.vesselOut?.miscRevenue)
      : 0;
    const FuelSettlementProfit = fleetDetails?.fuelSettlementProfit
      ? Number(fleetDetails?.fuelSettlementProfit)
      : 0;
    const FuelHandOverProfit = TotalFuelHandOverProfit;
    const totalRevenue =
      TCOGrossHire +
      TCOHireCommission +
      miscRevenue +
      FuelSettlementProfit +
      FuelHandOverProfit;
    // expense
    const fuelExpenses = fleetDetails?.fuelExpense
      ? Number(fleetDetails?.fuelExpense)
      : 0;
    const hireExpenses = fleetDetails?.hireExpense
      ? Number(fleetDetails?.hireExpense)
      : 0;
    const portExpenses = Number(totalPortExpense);
    const others = fleetDetails?.otherExpense
      ? Number(fleetDetails?.otherExpense)
      : 0;
    const totalExpenses = fuelExpenses + hireExpenses + portExpenses + others;

    // profit
    const totalDur =
      Number(fleetDetails?.vesselOut?.DurationOut || 0) +
      Number(fleetDetails?.voyageDuration || 0);

    const profit =
      totalRevenue -
      (totalExpenses < 0 ? totalExpenses * -1 : totalExpenses);
    const profitPerDay =
      profit / ((totalDur < 0 ? totalDur * -1 : totalDur) || 1);
    const voyDuration = totalDur;
    const hsQty = fleetDetails?.hsQuantity;
    const lsQty = fleetDetails?.lsQuantity;
    const mgoQty = fleetDetails?.mgoQuantity;

    const FuelHandOverProfitCount = {
      bunkerSuppliedTotal: {
        bunkerSuppliedHSFOTotal,
        bunkerSuppliedLSFOTotal,
        bunkerSuppliedMGOTotal,
      },
      bunkerSuppliedInternalTotal: {
        bunkerSuppliedHSFOSupplyInternalTotal,
        bunkerSuppliedLSFOSupplyInternalTotal,
        bunkerSuppliedMGOSupplyInternalTotal,
      },
      reserveInternalTotal: {
        hsReserveInternalTotal,
        lsReserveInternalTotal,
        mgoReserveInternalTotal,
      },
      voyConsumptionFuelQty,
      supplyBalanceFuelQty,
      balanceFuelQty,
      voyConsumptionFuelPrice,
      supplyBalanceFuelCost,
      balanceFuelCost,
      fuelPrice,
      deliveryOutFuelCost,
      fuelHandOverProfit,
      TotalFuelHandOverProfit,
      TCOGrossHire,
      TCOHireCommission,
      miscRevenue,
      FuelSettlementProfit,
      FuelHandOverProfit,
      totalRevenue,
      fuelExpenses,
      others,
      portExpenses,
      hireExpenses,
      totalExpenses,
      profit,
      profitPerDay,
      voyDuration,
      hsQty,
      lsQty,
      mgoQty,
    };

    return FuelHandOverProfitCount;
  };

  // ========== PORT CANAL & DISTANCE ==========

  const onChangePortCanalInQueryState = (portCanalChecks) => {
    setAllQueryFormat([
      {
        ...allQueryFormat,
        ...portCanalChecks,
      },
    ]);
    setSelectedQuery({
      ...allQueryFormat,
      ...portCanalChecks,
    });
  };

  const onFetchPortOperationDistance = (
    reset = false,
    propCalculator = null,
    portCanalChecks = {},
    isSave = false
  ) => {
    let updatedCalculatorData = { ...stateRef.current.calculatorData };
    if (propCalculator) {
      updatedCalculatorData = { ...propCalculator };
    }

    const { getPortDistanceString, routeList } =
      convertPortOperationToDistanceString(updatedCalculatorData);

    const { clientQuery = {} } = updatedCalculatorData;
    const {
      eca = false,
      hra = false,
      jwc = false,
      suez = false,
      panama = false,
      singapore = false,
    } = clientQuery;
    const portDataObj = {
      eca,
      hra,
      jwc,
      suez,
      panama,
      singapore,
      routeOverride: false,
      getGeometry: false,
      ...portCanalChecks,
      routeList: routeList,
      portList: getPortDistanceString,
      clientQueryId: id || propVesselId,
      contractId: id || propVesselId,
    };

    let request = "";

    onChangePortCanalInQueryState(portCanalChecks);
    if (reset)
      request = resetPortDistanceForContractApi({
        contractId: id || propVesselId,
      });
    else request = updatePortDistanceForContractApi(portDataObj);
    if (!loader) setLoader(true, "Recalculating port distances...");
    request
      .then((res) => {
        setLoader(false);
        setPortDistanceResponse(res.data);
        setRealPortDistanceResponse(res.realData);
        setPortDistancePayloadResponse(res.payload);
        if (reset) {
          updatedCalculatorData = {
            ...updatedCalculatorData,
            clientQuery: {
              ...updatedCalculatorData.clientQuery,
              portToPortDistance: {
                ...updatedCalculatorData.clientQuery.portToPortDistance,
                output: res.data,
              },
            },
          };
        }
        let realData = reset
          ? JSON.parse(JSON.stringify(res.data))
          : res.realData;
        const newPortOperations = addVesselVesselPortDistanceToOperations(
          res.data,
          [...updatedCalculatorData.portOperation],
          realData,
          false,
          reset
        );
        sendCalculationRequest({
          ...updatedCalculatorData,
          clientQuery: {
            ...updatedCalculatorData.clientQuery,
            portToPortDistanceReal: {
              payload: res.payload,
              output: realData,
            },
            portToPortDistance: {
              payload: res.payload,
              output: res.data,
            },
          },
          portOperation: newPortOperations,
          portDistanceResponse: res.data,
          autoSave: isSave,
          disableRefetch: isSave,
        });
      })
      .catch(() => setLoader(false));
  };

  const onFetchAndSetIntitalPortDistance = (propCalculator) => {
    if (!fetchPortDistance.current) return;
    let updatedCalculatorData = { ...propCalculator };
    const { getPortDistanceString, routeList } =
      convertPortOperationToDistanceString(updatedCalculatorData);
    setLoader(true, "Calculating port distances...");

    return new Promise((resolve, reject) => {
      const { clientQuery = {} } = updatedCalculatorData;
      const {
        eca = false,
        hra = false,
        jwc = false,
        suez = false,
        panama = false,
        singapore = false,
      } = clientQuery;
      const portDataObj = {
        eca,
        hra,
        jwc,
        suez,
        panama,
        singapore,
        routeOverride: false,
        routeList: routeList,
        portList: getPortDistanceString,
        contractId: id || propVesselId,
        queryId: id || propVesselId,
        getGeometry: false,
      };
      updateVesselPortDistanceForContractApi(portDataObj)
        .then((res) => {
          fetchPortDistance.current = false;
          setPortDistanceResponse(res.data);
          setRealPortDistanceResponse(res.realData);
          setPortDistancePayloadResponse(res.payload);
          resolve(res);
        })
        .catch((err) => {
          fetchPortDistance.current = false;
          setLoader(false);
          resolve({ error: true });
          toastError(err?.data?.message || "Something went wrong");
        });
    });
  };

  // ========== ADD / DELETE OPERATIONS ==========

  const onAddOperations = (requiredData = {}) => {
    setLoader(true, "Adding new operation...");
    fetchPortDistance.current = true;
    addCargoForEstimateApi({
      details: {
        ...initialCreatePortOperation,
        ...requiredData,
      },
      contractId: id || propVesselId,
      contractType: "vessel-vessel",
      addIndex: openAddOperationIndex,
    })
      .then((response) => {
        const updatedSelectedQuery = {
          ...selectedQuery,
          calculationFormat:
            response.data?.vesselDetails?.clientQuery?.calculationFormat,
        };
        setLoader(false);

        const portOperations = placeAddedOperation(
          stateRef.current.calculatorData.portOperation,
          response.data?.vesselDetails?.clientQuery?.cargoList || [],
          openAddOperationIndex
        );

        const formattedPortOperations = portOperations.map((operation) => {
          const cargoDetails = operation.cargoDetails || {};
          return {
            ...operation,
            cargoDetails: { ...cargoDetails },
          };
        });

        const updatedCalculatorData = {
          ...stateRef.current.calculatorData,
          clientQuery: updatedSelectedQuery,
          portOperation: formattedPortOperations,
        };

        sendCalculationRequest(updatedCalculatorData);
      })
      .catch((err) => {
        setLoader(false);
      });
  };

  const onDeleteOperations = (index, cargoId) => {
    setLoader(true, "Deleting operation...");
    fetchPortDistance.current = true;
    deleteCargoFromEstimateByIdApi({
      contractId: id || propVesselId,
      cargoId: index,
      contractType: "vessel-vessel",
    })
      .then((res) => {
        setLoader(false);
        let portOperation = [
          ...stateRef.current.calculatorData.portOperation,
        ];
        portOperation.splice(index, 1);
        const updatedSelectedQuery = {
          ...selectedQuery,
          calculationFormat: res.data.calculationFormat,
        };

        setCalculatorData({
          ...stateRef.current.calculatorData,
          portOperation,
          clientQuery: updatedSelectedQuery,
        });
      })
      .catch((err) => setLoader(false));
  };

  // ========== CALCULATE BUTTONS ==========

  const onClickCalculate = () => {
    sendCalculationRequest({
      ...stateRef.current.calculatorData,
      autoSave: false,
    });
  };

  const onClickCalculateAndSave = async () => {
    await sendCalculationRequest({
      ...stateRef.current.calculatorData,
      autoSave: true,
    });
  };

  // ========== VESSEL CHANGE HANDLERS ==========

  const onChangeSelectedVessel = (e, isFleetEdit = false) => {
    const {
      name = "",
      value = "",
      type = "number",
      checked = false,
    } = e.target || {};
    let newUpdatedFleet = { ...stateRef.current.selectedVessels };
    const { fuelType = "" } =
      stateRef.current.calculatorData.clientQuery || {};
    let updatedvessel = { ...JSON.parse(newUpdatedFleet.vessel) };

    if (isFleetEdit) {
      newUpdatedFleet[name] = value;
    } else {
      if (type === "checkbox") {
        updatedvessel[name] = checked;
        if (
          name === "vesselBalComb1Selected" ||
          name === "vesselBalComb2Selected" ||
          name === "vesselBalComb3Selected" ||
          name === "vesselBalComb4Selected"
        ) {
          if (
            !updatedvessel["vesselBalComb1Selected"] &&
            !updatedvessel["vesselBalComb2Selected"] &&
            !updatedvessel["vesselBalComb3Selected"] &&
            !updatedvessel["vesselBalComb4Selected"]
          ) {
            return;
          }
        } else if (
          name === "vesselLadComb1Selected" ||
          name === "vesselLadComb2Selected" ||
          name === "vesselLadComb3Selected" ||
          name === "vesselLadComb4Selected"
        ) {
          if (
            !updatedvessel["vesselLadComb1Selected"] &&
            !updatedvessel["vesselLadComb2Selected"] &&
            !updatedvessel["vesselLadComb3Selected"] &&
            !updatedvessel["vesselLadComb4Selected"]
          ) {
            return;
          }
        }
      } else {
        updatedvessel[name] = value;
      }
    }
    const updatedFleet = {
      ...newUpdatedFleet,
      vessel: JSON.stringify(updatedvessel),
    };
    setSelectedVessels(updatedFleet);
    const updatedFleetList = stateRef.current.calculatorData.fleetList.map(
      (item) => {
        if (item._id === updatedFleet._id) {
          return updatedFleet;
        }
        return item;
      }
    );
    let updatedPortOperation = [
      ...stateRef.current.calculatorData.portOperation,
    ];
    if (
      name === "bnkPriceDeliveryhs" ||
      name === "bnkPriceDeliveryls" ||
      name === "bnkPriceDeliverymgo"
    ) {
      const currentType = fuelType.replace("TYPE", "");
      if (name === "bnkPriceDeliveryhs")
        updatedPortOperation[0].cargoDetails[
          `fuelPriceType${currentType}Hsfo`
        ] = value;
      if (name === "bnkPriceDeliveryls")
        updatedPortOperation[0].cargoDetails[
          `fuelPriceType${currentType}Lsfo`
        ] = value;
      if (name === "bnkPriceDeliverymgo")
        updatedPortOperation[0].cargoDetails[
          `fuelPriceType${currentType}Mgo`
        ] = value;
    }
    if (name === "bnkDelhs" || name === "bnkDells" || name === "bnkDelmgo") {
      if (updatedPortOperation.length) {
        if (name === "bnkDelhs")
          updatedPortOperation[0].cargoDetails.bunkerSuppliedHSFO = value;
        if (name === "bnkDells")
          updatedPortOperation[0].cargoDetails.bunkerSuppliedLSFO = value;
        if (name === "bnkDelmgo")
          updatedPortOperation[0].cargoDetails.bunkerSuppliedMGO = value;
      }
    }
    setCalculatorData({
      ...stateRef.current.calculatorData,
      portOperation: updatedPortOperation,
      fleetList: updatedFleetList,
    });
  };

  const onChangeSelectedVesselOut = (e, isFleetEdit = false) => {
    const {
      name = "",
      value = "",
      type = "number",
      checked = false,
    } = e.target || {};
    let updatedvessel = { ...stateRef.current.selectedVessels?.vesselOut };
    const { fuelType = "" } =
      stateRef.current.calculatorData.clientQuery || {};

    updatedvessel[name] = value;

    const updatedFleet = {
      ...stateRef.current.selectedVessels,
      vesselOut: updatedvessel,
    };
    setSelectedVessels(updatedFleet);
    setCalculatorData({
      ...stateRef.current.calculatorData,
      fleetList: [updatedFleet],
    });
  };

  // ========== SYNC BUNKER ==========

  const onChangeSyncBunker = (checked) => {
    let updatedClientQuery = {
      ...stateRef.current.calculatorData.clientQuery,
    };

    let updatedCalculatorData = { ...stateRef.current.calculatorData };

    let clientQuery = {
      ...updatedCalculatorData.clientQuery,
      fuelPriceOverridden: checked,
      fuelCalculationMethod:
        !updatedCalculatorData.clientQuery.fuelCalculationMethod || updatedCalculatorData.clientQuery.fuelCalculationMethod === "DEFAULT"
          ? "LIFO"
          : updatedCalculatorData.clientQuery.fuelCalculationMethod,
    };
    if (!checked) {
      clientQuery = {
        ...updatedCalculatorData.clientQuery,
        fuelPriceOverridden: checked,
        fuelCalculationMethod: "DEFAULT",
      };
    }

    updatedCalculatorData = {
      ...updatedCalculatorData,
      clientQuery,
    };
    setCalculatorData(updatedCalculatorData);
  };

  // ========== SPLIT HIRE ==========

  const openSplitHireModal = (fleet) => {
    setSelectedSplitHire(fleet);
    setSplitHireModal(true);
  };

  const openSplitHireModalVesselOut = (fleet) => {
    setSelectedSplitHireVesselOut(fleet);
    setSplitHireModalVesselOut(true);
  };

  const addNewSplitHire = () => {
    let splitHire =
      (selectedSplitHire &&
        selectedSplitHire.splitHire &&
        JSON.parse(selectedSplitHire.splitHire)) ||
      [];
    if (!!splitHire.find((item) => item.NoDays === 0)) return;
    splitHire.push({ NoDays: 0, applied: 0, disable: false, hireRate: 0 });
    setSelectedSplitHire({
      ...selectedSplitHire,
      splitHire: JSON.stringify(splitHire),
    });
  };

  const addNewSplitHireVesselOut = () => {
    let splitHire =
      (selectedSplitHireVesselOut &&
        selectedSplitHireVesselOut.vesselOut.splitHire &&
        JSON.parse(selectedSplitHireVesselOut.vesselOut.splitHire)) ||
      [];
    if (!!splitHire.find((item) => item.NoDays === 0)) return;
    splitHire.push({ NoDays: 0, applied: 0, disable: false, hireRate: 0 });
    setSelectedSplitHireVesselOut({
      ...selectedSplitHireVesselOut,
      vesselOut: {
        ...selectedSplitHireVesselOut?.vesselOut,
        splitHire: JSON.stringify(splitHire),
      },
    });
  };

  const onChangeSplitHire = (index, e, isDelete = false) => {
    let splitHire =
      (selectedSplitHire &&
        selectedSplitHire.splitHire &&
        JSON.parse(selectedSplitHire.splitHire)) ||
      [];
    if (isDelete) splitHire.splice(index, 1);
    else {
      const { name = "", value = "" } = e.target;
      splitHire[index][name] = Number(value);
    }
    const udpatedSelectedSplitHire = {
      ...selectedSplitHire,
      splitHire: JSON.stringify(splitHire),
    };
    setSelectedSplitHire(udpatedSelectedSplitHire);

    setCalculatorData({
      ...stateRef.current.calculatorData,
      fleetList: stateRef.current.calculatorData.fleetList.map((item) => {
        if (item._id === udpatedSelectedSplitHire._id) {
          return udpatedSelectedSplitHire;
        }
        return item;
      }),
    });
    if (
      udpatedSelectedSplitHire._id === stateRef.current.selectedVessels._id
    )
      setSelectedVessels(udpatedSelectedSplitHire);
  };

  const onChangeSplitHireVesselOut = (index, e, isDelete = false) => {
    let splitHire =
      (selectedSplitHireVesselOut &&
        selectedSplitHireVesselOut.vesselOut.splitHire &&
        JSON.parse(selectedSplitHireVesselOut.vesselOut.splitHire)) ||
      [];
    if (isDelete) splitHire.splice(index, 1);
    else {
      const { name = "", value = "" } = e.target;
      splitHire[index][name] = Number(value);
    }
    const udpatedSelectedSplitHire = {
      ...selectedSplitHireVesselOut,
      vesselOut: {
        ...selectedSplitHireVesselOut?.vesselOut,
        splitHire: JSON.stringify(splitHire),
      },
    };
    setSelectedSplitHireVesselOut(udpatedSelectedSplitHire);

    setCalculatorData({
      ...stateRef.current.calculatorData,
      fleetList: stateRef.current.calculatorData.fleetList.map((item) => {
        if (item._id === udpatedSelectedSplitHire._id) {
          return udpatedSelectedSplitHire;
        }
        return item;
      }),
    });
    if (
      udpatedSelectedSplitHire._id === stateRef.current.selectedVessels._id
    )
      setSelectedVessels(udpatedSelectedSplitHire);
  };

  // ========== OVERRIDE ==========

  const onChangeOverride = (value) => {
    if (!value) {
      const {
        clientQuery = {},
        portOperation,
        fleetList,
      } = { ...stateRef.current.calculatorData } || {};
      let {
        fuelCalculationMethod = "",
        fuelType = "TYPE0",
        lsFuelPriceType0 = 0,
        lsFuelPriceType1 = 0,
        lsFuelPriceType2 = 0,
        lsFuelPriceType3 = 0,
        lsFuelPriceType4 = 0,
        hsFuelPriceType0 = 0,
        hsFuelPriceType1 = 0,
        hsFuelPriceType2 = 0,
        hsFuelPriceType3 = 0,
        hsFuelPriceType4 = 0,
        mgoFuelPriceType0 = 0,
        mgoFuelPriceType1 = 0,
        mgoFuelPriceType2 = 0,
        mgoFuelPriceType3 = 0,
        mgoFuelPriceType4 = 0,
      } = clientQuery || {};

      let fuelTypePrices = {
        TYPE0: {
          normal: hsFuelPriceType0,
          eca: lsFuelPriceType0,
          mgo: mgoFuelPriceType0,
        },
        TYPE1: {
          normal: hsFuelPriceType1,
          eca: lsFuelPriceType1,
          mgo: mgoFuelPriceType1,
        },
        TYPE2: {
          normal: hsFuelPriceType2,
          eca: lsFuelPriceType2,
          mgo: mgoFuelPriceType2,
        },
        TYPE3: {
          normal: hsFuelPriceType3,
          eca: lsFuelPriceType3,
          mgo: mgoFuelPriceType3,
        },
        TYPE4: {
          normal: hsFuelPriceType4,
          eca: lsFuelPriceType4,
          mgo: mgoFuelPriceType4,
        },
      };

      const selectedFuelPrice = fuelTypePrices[fuelType];
      const newPortOperation = [...portOperation];
      const currentType = fuelType.replace("TYPE", "");
      newPortOperation[0].cargoDetails[
        `fuelPriceType${currentType}Hsfo`
      ] = selectedFuelPrice.normal;
      newPortOperation[0].cargoDetails[
        `fuelPriceType${currentType}Lsfo`
      ] = selectedFuelPrice.eca;
      newPortOperation[0].cargoDetails[
        `fuelPriceType${currentType}Mgo`
      ] = selectedFuelPrice.mgo;

      let newSelectedVessel = {
        ...stateRef.current.selectedVessels,
        bunkerPriceOverridden: false,
        bnkPriceDeliveryhs: selectedFuelPrice.normal,
        bnkPriceDeliveryls: selectedFuelPrice.eca,
        bnkPriceDeliverymgo: selectedFuelPrice.mgo,
        bnkPriceRedeliveryhs: selectedFuelPrice.normal,
        bnkPriceRedeliveryls: selectedFuelPrice.eca,
        bnkPriceRedeliverymgo: selectedFuelPrice.mgo,
        bnkPriceSettlemenths: selectedFuelPrice.normal,
        bnkPriceSettlementls: selectedFuelPrice.eca,
        bnkPriceSettlementmgo: selectedFuelPrice.mgo,
      };

      let updatedFleets = fleetList.map((item) => {
        if (item._id === newSelectedVessel._id) return newSelectedVessel;
        return item;
      });
      setSelectedVessels(newSelectedVessel);
      setCalculatorData({
        ...stateRef.current.calculatorData,
        portOperation: newPortOperation,
        fleetList: updatedFleets,
      });
    } else {
      const { fleetList } =
        { ...stateRef.current.calculatorData } || {};
      let newSelectedVessel = {
        ...stateRef.current.selectedVessels,
        bunkerPriceOverridden: true,
      };

      let updatedFleets = fleetList.map((item) => {
        if (item._id === newSelectedVessel._id) return newSelectedVessel;
        return item;
      });
      setSelectedVessels(newSelectedVessel);
      setCalculatorData({
        ...stateRef.current.calculatorData,
        fleetList: updatedFleets,
      });
    }
    setIsOverride(value);
  };

  // ========== HIDE CALCULATOR / MAP ==========

  const onSetHideCalculator = () => {
    setHideCalculator(!hideCalculator);
  };

  const onSetOpenMap = () => {
    const data = stateRef.current.calculatorData;
    openMapInNewTab(id || propVesselId, {
      portOperation: data?.portOperation,
      fleetList: data?.fleetList,
      portDistanceResponse: data?.clientQuery?.portToPortDistance?.output,
    });
  };

  // ========== VESSEL CONTRACT MODAL ==========

  const onClickVesselContract = async (type) => {
    setEditVesselContractModelOpen(true);
    const data =
      type === "vesselIn"
        ? stateRef.current.rawEstimateContract?.vesselContractDetails
        : {
            ...stateRef.current.vesselOutContract,
            _id: stateRef.current.rawEstimateContract
              ?.vesselContractDetails?._id,
          };
    const contractData = data
      ? {
          ...data,
          cpDate:
            data.cpDate ||
            data.estimationDate ||
            data.contractDetails?.cpDate ||
            data.vesselDetails?.cpDate ||
            "",
        }
      : {};
    setVesselFormData({
      [type]: contractData,
      type,
    });
    setPrevAttachments({
      attachments: [...(data?.attachments || [])],
      textContract: data?.textContract,
    });
  };

  const handleUpdateVesselContract = () => {
    const { _id, ...payload } = vesselFormData[vesselFormData.type];
    const formPayload = new FormData();

    if (vesselTextContract) {
      const fileType =
        vesselTextContract.type || "application/octet-stream";
      const fileName = vesselTextContract.name || "contract";

      const textBlob = new Blob([vesselTextContract], { type: fileType });
      const textFile = new File([textBlob], fileName, { type: fileType });

      formPayload.append("files", textFile);
      formPayload.append(
        "textContractMetadata",
        JSON.stringify({ fileType: "textContract" })
      );
    }

    if (vesselAttachments?.length) {
      vesselAttachments.forEach((file) => {
        formPayload.append("files", file);
      });
    }

    formPayload.append(
      "data",
      JSON.stringify({
        ...payload,
        type: vesselFormData.type,
        isEstimateSavedContract: true,
        attachments: prevAttachments.attachments,
        textContract: prevAttachments.textContract,
        organizationId,
      })
    );

    updateVesselContractApi({ _id }, formPayload)
      .then(() => {
        setEditVesselContractModelOpen(false);
        setVesselAttachments([]);
        setVesselTextContract("");
        setPrevAttachments({
          attachments: [],
          textContract: "",
        });
        getInitalQueryAndFleetData(id || propVesselId);
        toastSuccess("Vessel Contract Updated successfully!!");
      })
      .catch((err) => {
        toastError(err.message);
      });
  };

  const removeVesselAttachment = (index) => {
    setVesselAttachments((prev) => (Array.isArray(prev) ? prev : []).filter((_, i) => i !== index));
  };

  const handleVesselContractInputChange = (e) => {
    const { name, value } = e.target;
    setVesselFormData({
      ...vesselFormData,
      [vesselFormData.type]: {
        ...vesselFormData[vesselFormData.type],
        [name]: value,
      },
    });
  };

  const handleVesselTextContractUpload = (e) => {
    if (e.target.files[0]) {
      setVesselTextContract(e.target.files[0]);
    }
  };

  const handleVesselContractAttachmentUpload = (e) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      const validFiles = newFiles.filter(
        (file) => file.size <= 5 * 1024 * 1024
      );

      if (validFiles.length !== newFiles.length) {
        alert("Some files exceed the 5 MB size limit and were not added.");
      }

      setVesselAttachments((prev) => [...(Array.isArray(prev) ? prev : []), ...validFiles]);
    }
  };

  // ========== COPY TO ESTIMATION ==========

  const onClickCopyToEstimate = () => {
    setEstimationCopyModelOpen(true);
  };

  const handleCopyToEstimation = () => {
    const {
      _id,
      userId,
      access,
      createdAt,
      contractName,
      updateAt,
      ...rest
    } = stateRef.current.rawEstimateContract;
    const payload = {
      contractName: inputValue,
      ...rest,
    };
    createEstimateContractApi(payload)
      .then(() => {
        toastSuccess("Estimation copied Successfully");
        setEstimationCopyModelOpen(false);
      })
      .catch((err) => {
        toastError("Failed to copy estimation to estimate contract");
      });
  };

  // ========== RETURN ==========

  return {
    // state
    id: id || propVesselId,
    calculatorData,
    setCalculatorData,
    selectedVessels,
    setSelectedVessels,
    syncBunker,
    setSyncBunker,
    isOverride,
    isPrivate,
    calculationErrors,

    // ui state
    hideOrder,
    setHideOrder,
    hideCalculator,
    fleetCheckbox,
    setFleetCheckbox,

    // modals
    marginModal,
    setMarginModal,
    supplyModal,
    setSupplyModal,
    splitHireModal,
    setSplitHireModal,
    splitHireModalVesselOut,
    setSplitHireModalVesselOut,
    selectedSplitHire,
    selectedSplitHireVesselOut,
    openAddOperation,
    setOpenAddOperation,
    setOpenAddOperationindex,
    editVesselContractModelOpen,
    setEditVesselContractModelOpen,
    estimationCopyModelOpen,
    setEstimationCopyModelOpen,
    inputValue,
    setInputValue,

    // contract form state
    vesselFormData,
    setVesselFormData,
    vesselAttachments,
    vesselTextContract,
    prevAttachments,
    setPrevAttachments,

    // props pass-through
    tabs,
    active,
    handleMainTabChange,
    propVesselId,
    isViewOnly,

    // refs
    fetchPortDistance,

    // handlers
    onChangePortOperations,
    sendCalculationRequest,
    onClickCalculate,
    onClickCalculateAndSave,
    onChangeSelectedVessel,
    onChangeSelectedVesselOut,
    onChangeSyncBunker,
    onChangeOverride,
    onFetchPortOperationDistance,
    onAddOperations,
    onDeleteOperations,
    onSetHideCalculator,
    onSetOpenMap,
    onChangePortCanalInQueryState,
    openSplitHireModal,
    openSplitHireModalVesselOut,
    addNewSplitHire,
    addNewSplitHireVesselOut,
    onChangeSplitHire,
    onChangeSplitHireVesselOut,
    onClickVesselContract,
    handleUpdateVesselContract,
    removeVesselAttachment,
    handleVesselContractInputChange,
    handleVesselTextContractUpload,
    handleVesselContractAttachmentUpload,
    onClickCopyToEstimate,
    handleCopyToEstimation,
  };
}
